/**
 * Временные заглушки для AppHeader и состояние шапки.
 * По мере готовности бэка — методы переезжают в соответствующие Service/DAO.
 */

import { ref } from 'vue';
import type { Ref } from 'vue';
import Core from '@/core/Core'; // TODO: поправить путь под фактическое расположение Core
import router from "@/router";
import {RouteRecordRaw} from "vue-router";
import User from "@/core/services/Acl/User";

export type NavRoute = {
  path:      string;
  label:     string;
  icon?:     string;
  children?: NavRoute[];
  hash?:     string;
};

export type NavGroup = {
  label:       string;
  items:       NavRoute[];
  /** Доступность воронки текущему партнёру. Лямбда — чтобы вычислялось лениво, после загрузки Core.currentPartner. */
  isAvailable: () => boolean;
};

const ROLE_STORAGE_KEY = 'app-header:current-role';

// Синглтон reactive-ref текущей роли. Инициализируется lazily при первом обращении —
// до того как вызван loadInitialRole(), держит первую группу из availableFunnels.
let currentRoleRef: Ref<string> | null = null;

class AppHeaderService {
  static readonly REFS_FAVORITES_STORAGE_KEY = 'app-header:refs:favorites';

  // Сброс состояния роли при выходе — currentRoleRef переживает сессию,
  // при входе под другим партнёром роль должна пересчитаться заново.
  static init(): void {
    Core.eventBus.AclSessionExpired.addEventListener(() => AppHeaderService.reset());
    Core.eventBus.AclLogout.addEventListener(() => AppHeaderService.reset());
  }

  /** Инвалидация reactive-состояния роли. Следующее обращение к currentRoleLabel пересоздаст ref. */
  static reset(): void {
    currentRoleRef = null;
  }

  /** Справочники. icon - имя SVG из src/assets/icons-set, рендерится через VSvgIcon */
  static getStubRefs(): NavRoute[] {
    return [
      { path: '/list/changelogs',           label: 'Активность партнеров',    icon: 'manage-accounts'      },
      { path: '/list/budget-prices',        label: 'Бюджетные цены',          icon: 'prices'               },
      { path: '/list/drivers',              label: 'Водители',                icon: 'drivers'              },
      { path: '/list/contracts-external',   label: 'Договоры',                icon: 'contracts'            },
      { path: '/list/units',                label: 'Ед. измерения',           icon: 'straighten'           },
      { path: '/list/characteristic-units', label: 'Ед. изм. характеристик',  icon: 'design-services'      },
      { path: '/flow/orders-v2',            label: 'Заказы 2',                icon: 'pallet'               },
      { path: '/list/transport-zones',      label: 'Зоны транспортные',       icon: 'location-on'          },
      { path: '/list/transport-orders',     label: 'ЗТК',                     icon: 'assignment-add'       },
      { path: '/list/product-categories',   label: 'Категории',               icon: 'category'             },
      { path: '/list/leads',                label: 'Лиды',                    icon: 'group-add'            },
      { path: '/list/demands',              label: 'Листы потребностей',      icon: 'checklist-rtl'        },
      { path: '/list/transport-routes',     label: 'Маршруты',                icon: 'route'                },
      { path: '/list/products',             label: 'Номенклатура',            icon: 'contract'             },
      { path: '/list/demandsV2',            label: 'Обеспечение потребностей',icon: 'checklist-rtl'        },
      { path: '/list/shipments',            label: 'Отгрузки',                icon: 'deployed-code-update' },
      { path: '/list/reports',              label: 'Отчёты',                  icon: 'list-alt-check'       },
      { path: '/list/contracts-public',     label: 'Оферты',                  icon: 'offers'               },
      { path: '/list/partners',             label: 'Партнеры',                icon: 'partners'             },
      { path: '/list/payments',             label: 'Платежи',                 icon: 'payments'             },
      { path: '/list/order-positions',      label: 'Позиции заказов',         icon: 'trolley'              },
      { path: '/list/preorders',            label: 'Предзаказы',              icon: 'trolley'              },
      { path: '/list/receipts',             label: 'Приемки',                 icon: 'order-approve'        },
      { path: '/list/product-code-mappings',label: 'Продуктовые коды',        icon: 'barcode-scanner'      },
      { path: '/list/manufacturers',        label: 'Производители',           icon: 'factory'              },
      { path: '/list/production-plans',     label: 'Производственные планы',  icon: 'calendar-plans'       },
      { path: '/list/transport-downtimes',  label: 'Простои',                 icon: 'timer-pause'          },
      { path: '/list/supply-offers',        label: 'Разрешения на поставку',  icon: 'supply-permissions'   },
      { path: '/list/shipments-to-buyer',   label: 'Реализации',              icon: 'move-shopping-cart'   },
      { path: '/list/assemblies',           label: 'Сборочные листы',         icon: 'format-list-bulleted' },
      { path: '/list/shipments-reconciliation', label: 'Сверка документов',   icon: 'list-alt-check'       },
      { path: '/list/stores',               label: 'Склады',                  icon: 'warehouse'            },
      { path: '/list/tenders',              label: 'Тендеры',                 icon: 'tab-recent'           },
      { path: '/list/offers',               label: 'Товарные предложения',    icon: 'action-key'           },
      { path: '/list/trade-marks',          label: 'Торговые марки',          icon: 'circle'               },
      { path: '/list/vehicles',             label: 'Транспорт',               icon: 'local-shipping'       },
      { path: '/list/packages',             label: 'Упаковка',                icon: 'orders'               },
      { path: '/list/characteristics',      label: 'Характеристики',          icon: 'tune'                 },
    ];
  }

  static getSubRefsForCurrentUser(): NavRoute[] {
    /* Список юзеров, которым необходимо показывать все разделы в любом случае (пример: Валентине нужны все разделы для обучения коллег) */
    const exceptionUsers = [1629, 1520, 1521, 1630];

    const stubRefs = AppHeaderService.getStubRefs();
    const allRoutes = router.getRoutes();

    /* Отдаем список со всеми разделами */
    if (exceptionUsers.includes(Core.currentUser.id) === true) return stubRefs;

    return stubRefs.filter((stubRef: NavRoute) => {
      const route = allRoutes.find(route => route.path === stubRef.path);

      if (route === undefined) {
        return false;
      }

      return AppHeaderService.canAccessRoute((route.meta?.role ?? null) as string|null, (route.meta?.allowedPartnerIds ?? []) as number[], (route.meta?.mode ?? null) as string|null);
    });
  }

  /**
   * Метод проверяет есть ли доступ у текущего пользователя к роуту
   * @param routeRole - роль у которой есть доступ к роуту
   * @param routeAllowedPartnerIds - список идентификаторов партнеров которым доступен роут
   * @param mode - Определяется как 'and' || 'or'. читать условия вместе как and или or Пример 1: для админов и определенных партнеров; Пример 2: для админов или определенных партнеров (по дефолту 'or')
   */
  static canAccessRoute(routeRole: string | null, routeAllowedPartnerIds: number[], mode: string|null = null): boolean {
    const hasRoleRestriction = routeRole !== null;
    const hasPartnerRestriction = routeAllowedPartnerIds.length > 0;

    // Ограничений нет вообще -> доступ открыт
    if (!hasRoleRestriction && !hasPartnerRestriction) {
      return true;
    }

    // Задано только одно из ограничений -> оператор не имеет значения
    if (hasRoleRestriction && !hasPartnerRestriction) {
      return this.canAccessRouteRole(routeRole);
    }
    if (!hasRoleRestriction && hasPartnerRestriction) {
      return this.canAccessRoutePartnerId(routeAllowedPartnerIds);
    }

    // Заданы ОБА ограничения -> применяем указанный режим
    const hasRoleAccess = this.canAccessRouteRole(routeRole);
    const hasPartnerAccess = this.canAccessRoutePartnerId(routeAllowedPartnerIds);

    return mode === 'and'
      ? hasRoleAccess && hasPartnerAccess
      : hasRoleAccess || hasPartnerAccess;
  }

  private static canAccessRouteRole(routeRole: string | null): boolean {
    if (routeRole === null) {
      return true;
    }

    if (routeRole === 'buyer') {
      return Boolean(Core.currentPartner?.isBuyer);
    }

    if (routeRole === 'seller') {
      return Boolean(Core.currentPartner?.isSeller);
    }

    if (routeRole === 'admin') {
      return Boolean(Core.currentUser?.isAdmin || Core.currentUser?.isPrivileged || Core.currentPartner.isLSZ);
    }

    return false;
  }

  private static canAccessRoutePartnerId(routeAllowedPartnerIds: number[]): boolean {
    if (routeAllowedPartnerIds.length > 0) {
      return routeAllowedPartnerIds.includes(Core.currentPartner.id);
    }

    return false;
  }

  /** Справочники Favorites */
  static getStubDefaultFavorites(): NavRoute[] {
    return [];
  }

  /**
   * Воронки = роли. Заголовок группы используется как имя роли.
   *
   * Пути пунктов пишутся без сегмента вкладки: подсветка в шапке считается
   * по префиксу пути (isLinkActive), и пункт с вкладкой внутри гас бы на всех
   * остальных вкладках. Вкладку по умолчанию дописывает роутер редиректом.
   */
  static getStubFunnels(): NavGroup[] {
    return [
      {
        label: 'ОЗЛ',
        isAvailable: () => Core.currentPartner?.isSecondaryBuyer || Core.currentPartner?.isLSZ,
        items: [
          { path: '/list/products',                     label: 'Номенклатура',         icon: 'contract'             },
          { path: '/list/demands',                      label: 'Листы потребностей',   icon: 'checklist-rtl'        },
          { path: '/flow/tenders',                      label: 'Тендеры',              icon: 'tab-recent'           },
          { path: '/flow/preorders',                    label: 'Предзаказы',           icon: 'pallet'               },
          { path: '/flow/orders',                       label: 'Заказы',               icon: 'pallet'               },
          { path: '/flow/transport-orders/lsz', label: 'ЗТК',                  icon: 'assignment-add'       },
        ],
      },
      {
        label: 'Поставщик',
        isAvailable: () => Core.currentPartner?.isSeller,
        items: [
          { path: '/flow/offers',             label: 'Товарные предложения', icon: 'action-key'    },
          { path: '/list/demands',            label: 'Актуальный спрос',     icon: 'checklist-rtl' },
          { path: '/flow/tenders',            label: 'Тендеры',              icon: 'tab-recent'    },
          { path: '/flow/preorders',          label: 'Предзаказы',           icon: 'pallet'        },
          { path: '/flow/orders',     label: 'Заказы',               icon: 'pallet'        },
        ],
      },
      {
        label: 'Наманган',
        isAvailable: () => Core.currentPartner?.isNamangan === true,
        items: [
          { path: '/list/products',                     label: 'Номенклатура',     icon: 'contract'          },
          { path: '/demands-page/new-requests', label: 'Обеспечение потребностей', icon: 'add-shopping-cart' },
          { path: '/flow/orders',               label: 'Заказы',                   icon: 'pallet'            },
          { path: '/flow/transport-orders/namangan', label: 'ЗТК',                  icon: 'assignment-add'       },
          { path: '/list/transport-downtimes',       label: 'Простои',              icon: 'tab-recent'           },
        ],
      },
      {
        label: 'Электросталь ЗТК',
        isAvailable: () => Core.currentPartner?.isLSZ === true,
        items: [
          { path: '/flow/transportable-docs/electrostal',  label: 'Документы отгрузки',                    icon: 'unknown-document', },
          { path: '/flow/transport-orders/electrostal',    label: 'Консолидированные не доставленные ЗТК', icon: 'assignment-add',   },
        ],
      },
    ];
  }

  /** Воронки, доступные текущему партнёру. */
  static get availableFunnels(): NavGroup[] {
    return AppHeaderService.getStubFunnels().filter(item => item.isAvailable());
  }

  // ─── Текущая роль ───────────────────────────────────────────────────────

  /**
   * Reactive ref с label текущей роли. Используется компонентами шапки для
   * подписки на смену роли (при клике по заголовку группы в попапе воронок).
   */
  static get currentRoleLabel(): Ref<string> {
    if (currentRoleRef === null) {
      currentRoleRef = ref(AppHeaderService.loadInitialRoleLabel());
    }
    return currentRoleRef;
  }

  /** Роль целиком (label + items), найденная по текущему label среди доступных. Если не найдена — первая из доступных. */
  static get currentRole(): NavGroup {
    const funnels: NavGroup[] = AppHeaderService.availableFunnels;
    const found:   NavGroup | undefined = funnels.find(g => g.label === AppHeaderService.currentRoleLabel.value);
    if (found === undefined) {
      return funnels[0];
    } else {
      return found;
    }
  }

  /** Установить активную роль по label группы. Сохраняет в localStorage. */
  static setCurrentRoleLabel(label: string): void {
    AppHeaderService.currentRoleLabel.value = label;
    try {
      localStorage.setItem(ROLE_STORAGE_KEY, label);
    } catch {
      // localStorage недоступен — молча игнорируем
    }
  }

  /**
   * Считывает сохранённую роль из localStorage, если она есть среди доступных воронок,
   * либо возвращает label первой доступной воронки.
   */
  private static loadInitialRoleLabel(): string {
    const funnels: NavGroup[] = AppHeaderService.availableFunnels;
    try {
      const stored: string | null = localStorage.getItem(ROLE_STORAGE_KEY);
      if (stored !== null && funnels.some(g => g.label === stored)) {
        return stored;
      } else {
        return funnels[0].label;
      }
    } catch {
      return funnels[0].label;
    }
  }
}

export default AppHeaderService;
